import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import api from '../services/api';
import MovieCard from './MovieCard';
import { Typography, Box, Tooltip, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom';

const Section = styled.div`
  margin-bottom: 30px;
`;

const Title = styled(Typography)`
  margin-bottom: 15px;
  font-family: 'Carter One', cursive !important;
  font-weight: 400 !important;
  font-size: 1.5rem !important;
`;

const TitleLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const MovieRow = styled(Box)`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PlaceholderCard = styled(Box)`
  aspect-ratio: 2/3;
  background-color: rgba(0, 0, 0, 0.05);
  border: 2px dashed rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 10px;
  text-align: center;
  color: text.secondary;
`;

const WelcomeBanner = styled(Box)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;
const CardWrapper = styled.div`
  transition: all 0.3s ease-in-out;
  &.exiting {
    opacity: 0;
    transform: scale(0.9);
    pointer-events: none;
  }
`;

const KriticalWatching = ({
  user,
  onRatingUpdate,
  lastUpdate,
  title = 'Kritical Watching',
  tooltip = "These are the top five movies that you haven't watched or rated based on your Kritic matches.",
  getRecommendations = api.getKriticalWatching,
  hideWhenEmpty = false,
  showStarterExperience = true,
  titleLink,
  keepWannaWatch = false,
  emptyMessage = 'No matching movies are available right now.'
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exitingId, setExitingId] = useState(null);
  const userId = user?.id;

  const fetchRecs = useCallback(async (isRefresh = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }
    if (!isRefresh) setLoading(true);
    try {
      const data = await getRecommendations(userId);

      setRecommendations(prev => {
        // If we are getting the same data (starter vs personalized), we can handle it
        if (prev.length === 0) return data;

        const newDataIds = new Set(data.map(m => m.id));
        const preserved = prev.filter(m => newDataIds.has(m.id));
        const preservedIds = new Set(preserved.map(m => m.id));
        const newItems = data.filter(m => !preservedIds.has(m.id));

        return [...preserved, ...newItems];
      });
    } catch (error) {
      console.error(`Failed to fetch ${title} recommendations:`, error);
    } finally {
      setLoading(false);
    }
  }, [getRecommendations, title, userId]);

  useEffect(() => {
    // Only fetch on mount or when lastUpdate changes
    fetchRecs(recommendations.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRecs, lastUpdate]);

  const handleRating = (movieId, newRating, interaction = {}) => {
    if (keepWannaWatch && interaction.is_wanna_watch) {
      setRecommendations(previous => previous.map(movie => (
        movie.id === movieId ? { ...movie, is_wanna_watch: 1 } : movie
      )));
      if (onRatingUpdate) onRatingUpdate(movieId, newRating, interaction);
      return;
    }

    // 1. Trigger Exit Animation
    setExitingId(movieId);

    // 2. Wait for animation
    setTimeout(() => {
      // 3. Remove locally to trigger "shift"
      setRecommendations(prev => prev.filter(m => m.id !== movieId));
      setExitingId(null);

      // 4. Propagate to parent (updates DB -> triggers lastUpdate -> triggers fetchRecs)
      if (onRatingUpdate) onRatingUpdate(movieId, newRating, interaction);
    }, 300);
  };

  const hasRecs = recommendations.length > 0;
  const isStarter = showStarterExperience && hasRecs && recommendations[0].is_starter;
  const laneTitle = (
    <Title variant="h5" sx={{ mb: 0 }}>{title}</Title>
  );
  const linkedLaneTitle = titleLink ? (
    <TitleLink to={titleLink}>{laneTitle}</TitleLink>
  ) : laneTitle;

  if (loading && !hasRecs) {
    return null; // Or a skeleton loader
  }

  // If no recommendations (and not loading), show the empty state with placeholders
  if (!hasRecs) {
    if (hideWhenEmpty) return null;

    if (!showStarterExperience) {
      return (
        <Section>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            {linkedLaneTitle}
            <Tooltip title={tooltip} arrow>
              <IconButton size="small" sx={{ ml: 1, color: 'text.secondary', opacity: 0.7 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(0, 0, 0, 0.04)', color: 'text.secondary' }}>
            <Typography variant="body2">{emptyMessage}</Typography>
          </Box>
        </Section>
      );
    }

    return (
      <Section>
        <WelcomeBanner>
          <Typography variant="h6" fontWeight="bold">
            Welcome to Kritical Watching!
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Rate 5 more movies below to unlock your personalized recommendations here.
          </Typography>
        </WelcomeBanner>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, opacity: 0.5 }}>
          <Typography variant="h6" color="text.secondary">Kritical Watching Slots</Typography>
        </Box>

        <MovieRow sx={{ opacity: 0.6 }}>
          {[...Array(5)].map((_, index) => (
            <PlaceholderCard key={index}>
              <Typography variant="caption" color="text.secondary">Slot {index + 1}</Typography>
            </PlaceholderCard>
          ))}
        </MovieRow>
      </Section>
    );
  }

  return (
    <Section>
      {isStarter && (
        <WelcomeBanner>
          <Typography variant="h6" fontWeight="bold">
            Welcome to Kritical Watching!
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            Rate these blockbusters (or search below) to unlock your personalized recommendations.
          </Typography>
        </WelcomeBanner>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        {linkedLaneTitle}
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 1, color: 'text.secondary', opacity: 0.7 }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <MovieRow>
        {recommendations.slice(0, 5).map(movie => (
          <CardWrapper key={movie.id} className={exitingId === movie.id ? 'exiting' : ''}>
            <MovieCard
              movie={movie}
              onRatingUpdate={handleRating}
            />
          </CardWrapper>
        ))}
      </MovieRow>
    </Section>
  );
};

export default KriticalWatching;
